import type { PlayerId, RoundId, Session, SessionEvent } from '@/schemas';

export type EventHandlerContext = {
  session: Session;
  roundIndexById: Map<RoundId, number>;
  playerIndexById: Map<PlayerId, number>;
  resolveRoundIndex: (roundId: RoundId) => number | undefined;
  resolvePlayerIndex: (playerId: PlayerId) => number | undefined;
};

/**
 * Collect active players in the specified order
 */
const collectActivePlayersInOrder = (
  newOrder: PlayerId[],
  playerMap: Map<PlayerId, Session['players'][number]>,
): Session['players'] => {
  const activePlayers: Session['players'] = [];
  for (const playerId of newOrder) {
    const player = playerMap.get(playerId);
    if (player && !player.deletedAt) {
      activePlayers.push(player);
    }
  }
  return activePlayers;
};

/**
 * Collect players not in the new order (active and deleted separately)
 */
const collectPlayersNotInOrder = (
  draft: Session,
  newOrder: PlayerId[],
): {
  active: Session['players'];
  deleted: Session['players'];
} => {
  const active: Session['players'] = [];
  const deleted: Session['players'] = [];

  for (const player of draft.players) {
    if (newOrder.includes(player.id)) {
      continue;
    }
    if (player.deletedAt) {
      deleted.push(player);
    } else {
      active.push(player);
    }
  }

  return { active, deleted };
};

/**
 * Update player index map after reordering
 */
const updatePlayerIndexMap = (
  players: Session['players'],
  playerIndexById: Map<PlayerId, number>,
): void => {
  playerIndexById.clear();
  for (const [index, player] of players.entries()) {
    if (player) {
      playerIndexById.set(player.id, index);
    }
  }
};

/**
 * Handle PlayerOrderChanged event by reordering the players array
 */
const handlePlayerOrderChanged = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'PlayerOrderChanged' }>,
  playerIndexById: Map<PlayerId, number>,
): void => {
  const newOrder = event.payload.playerIds;

  // Validate that we have a non-empty order
  if (!newOrder || newOrder.length === 0) {
    // Invalid event - skip reordering
    return;
  }

  // Create a map of player ID to player object for quick lookup
  const playerMap = new Map(draft.players.map((player) => [player.id, player]));

  // Collect active players in the new order
  const activePlayersInOrder = collectActivePlayersInOrder(newOrder, playerMap);

  // Collect players not in the new order
  const { active: activeNotInOrder, deleted: deletedPlayers } = collectPlayersNotInOrder(
    draft,
    newOrder,
  );

  // Rebuild the players array: active players in new order, then active not in order, then deleted
  draft.players = [...activePlayersInOrder, ...activeNotInOrder, ...deletedPlayers];

  // Update the index map
  updatePlayerIndexMap(draft.players, playerIndexById);
};

/**
 * Handle player-related events (PlayerJoined, PlayerLeft, PlayerRemoved, PlayerOrderChanged)
 */
export const handlePlayerEvent = (
  draft: Session,
  event: SessionEvent,
  resolvePlayerIndex: (playerId: PlayerId) => number | undefined,
  playerIndexById: Map<PlayerId, number>,
): void => {
  switch (event.type) {
    case 'PlayerJoined': {
      const existingIndex = resolvePlayerIndex(event.payload.playerId);
      if (existingIndex === undefined) {
        const player = {
          id: event.payload.playerId,
          name: event.payload.name,
          nickname: event.payload.nickname,
          color: event.payload.color,
          userId: event.userId,
          createdAt: event.timestamp,
          updatedAt: event.timestamp,
          deletedAt: undefined,
        };
        const newIndex = draft.players.push(player) - 1;
        playerIndexById.set(player.id, newIndex);
      }
      break;
    }
    case 'PlayerLeft':
    case 'PlayerRemoved': {
      const playerIndex = resolvePlayerIndex(event.payload.playerId);
      if (playerIndex !== undefined) {
        const player = draft.players[playerIndex];
        if (player) {
          player.deletedAt = event.timestamp;
          player.updatedAt = event.timestamp;
        }
      }
      break;
    }
    case 'PlayerOrderChanged': {
      handlePlayerOrderChanged(draft, event, playerIndexById);
      break;
    }
  }
};

/**
 * Helper to handle RoundStarted event
 */
const handleRoundStarted = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundStarted' }>,
  roundIndexById: Map<RoundId, number>,
): void => {
  const resolveExistingIndex = (): number | undefined => {
    const cachedIndex = roundIndexById.get(event.payload.roundId);
    if (cachedIndex !== undefined && draft.rounds[cachedIndex]?.id === event.payload.roundId) {
      return cachedIndex;
    }
    const fallbackIndex = draft.rounds.findIndex((round) => round.id === event.payload.roundId);
    if (fallbackIndex !== -1) {
      roundIndexById.set(event.payload.roundId, fallbackIndex);
      return fallbackIndex;
    }
    return undefined;
  };

  const existingIndex = resolveExistingIndex();
  if (existingIndex !== undefined) {
    const round = draft.rounds[existingIndex];
    round.number = event.payload.number;
    round.name = event.payload.name;
    round.deletedAt = undefined;
    round.updatedAt = Math.max(round.updatedAt ?? event.timestamp, event.timestamp);
    draft.currentRoundNumber = Math.max(draft.currentRoundNumber, event.payload.number);
    return;
  }

  const round = {
    id: event.payload.roundId,
    sessionId: event.sessionId,
    number: event.payload.number,
    name: event.payload.name,
    scores: {},
    notes: undefined,
    tags: undefined,
    imageIds: [],
    createdAt: event.timestamp,
    updatedAt: event.timestamp,
    deletedAt: undefined,
  };
  const newRoundIndex = draft.rounds.push(round) - 1;
  draft.currentRoundNumber = Math.max(draft.currentRoundNumber, event.payload.number);
  roundIndexById.set(event.payload.roundId, newRoundIndex);
};

/**
 * Helper to handle RoundCompleted event
 */
const handleRoundCompleted = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundCompleted' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    draft.rounds[roundIndex].completedAt = event.timestamp;
  }
};

/**
 * Helper to handle RoundNoteAdded event
 */
const handleRoundNoteAdded = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundNoteAdded' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    draft.rounds[roundIndex].notes = event.payload.note;
  }
};

/**
 * Helper to handle RoundNoteRemoved event
 */
const handleRoundNoteRemoved = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundNoteRemoved' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    draft.rounds[roundIndex].notes = undefined;
  }
};

/**
 * Helper to handle RoundTagAdded event
 */
const handleRoundTagAdded = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundTagAdded' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    const round = draft.rounds[roundIndex];
    if (!round.tags) {
      round.tags = [];
    }
    if (!round.tags.includes(event.payload.tag)) {
      round.tags.push(event.payload.tag);
    }
  }
};

/**
 * Helper to handle RoundTagRemoved event
 */
const handleRoundTagRemoved = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundTagRemoved' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    const round = draft.rounds[roundIndex];
    round.tags = round.tags?.filter((tag) => tag !== event.payload.tag);
  }
};

/**
 * Helper to handle RoundDeleted event
 */
const handleRoundDeleted = (
  draft: Session,
  event: Extract<SessionEvent, { type: 'RoundDeleted' }>,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
): void => {
  const roundIndex = resolveRoundIndex(event.payload.roundId);
  if (roundIndex !== undefined) {
    draft.rounds[roundIndex].deletedAt = event.timestamp;
  }
};

/**
 * Handle round-related events
 */
export const handleRoundEvent = (
  draft: Session,
  event: SessionEvent,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
  roundIndexById: Map<RoundId, number>,
): void => {
  switch (event.type) {
    case 'RoundStarted': {
      handleRoundStarted(draft, event, roundIndexById);
      break;
    }
    case 'RoundCompleted': {
      handleRoundCompleted(draft, event, resolveRoundIndex);
      break;
    }
    case 'RoundNoteAdded': {
      handleRoundNoteAdded(draft, event, resolveRoundIndex);
      break;
    }
    case 'RoundNoteRemoved': {
      handleRoundNoteRemoved(draft, event, resolveRoundIndex);
      break;
    }
    case 'RoundTagAdded': {
      handleRoundTagAdded(draft, event, resolveRoundIndex);
      break;
    }
    case 'RoundTagRemoved': {
      handleRoundTagRemoved(draft, event, resolveRoundIndex);
      break;
    }
    case 'RoundDeleted': {
      handleRoundDeleted(draft, event, resolveRoundIndex);
      break;
    }
    // No default
  }
};

/**
 * Handle score-related events (ScoreChanged, ScoreSet)
 */
export const handleScoreEvent = (
  draft: Session,
  event: SessionEvent,
  resolveRoundIndex: (roundId: RoundId) => number | undefined,
  resolvePlayerId: (playerId: PlayerId) => PlayerId,
): void => {
  if (event.type === 'ScoreChanged') {
    const roundIndex = resolveRoundIndex(event.payload.roundId);
    if (roundIndex !== undefined) {
      const resolvedPlayerId = resolvePlayerId(event.payload.playerId);
      const currentScore = draft.rounds[roundIndex].scores[resolvedPlayerId] ?? 0;
      draft.rounds[roundIndex].scores[resolvedPlayerId] = currentScore + event.payload.delta;
    }
  } else if (event.type === 'ScoreSet') {
    const roundIndex = resolveRoundIndex(event.payload.roundId);
    if (roundIndex !== undefined) {
      const resolvedPlayerId = resolvePlayerId(event.payload.playerId);
      draft.rounds[roundIndex].scores[resolvedPlayerId] = event.payload.value;
    }
  }
};

/**
 * Handle spectator-related events (SpectatorJoined, SpectatorLeft)
 */
export const handleSpectatorEvent = (draft: Session, event: SessionEvent): void => {
  if (event.type === 'SpectatorJoined') {
    // Check if spectator already exists
    const existingSpectator = draft.spectators.find((s) => s.id === event.payload.spectatorId);
    if (!existingSpectator) {
      draft.spectators.push({
        id: event.payload.spectatorId,
        userId: event.payload.userId,
        joinedAt: event.timestamp,
      });
    }
  } else if (event.type === 'SpectatorLeft') {
    // Remove spectator from array
    const spectatorIndex = draft.spectators.findIndex((s) => s.id === event.payload.spectatorId);
    if (spectatorIndex !== -1) {
      draft.spectators.splice(spectatorIndex, 1);
    }
  }
};

/**
 * Handle invite token events (InviteTokenGenerated, InviteTokenRevoked, InviteLinkEnabled, InviteLinkDisabled)
 *
 * @remarks
 * These events affect SessionMetadata.inviteToken, not Session directly.
 * For now, these are no-ops in session state, but they may be used to track invite link state
 * in session metadata (handled separately in session store).
 */
export const handleInviteTokenEvent = (_draft: Session, _event: SessionEvent): void => {
  // Invite token state is stored in SessionMetadata, not Session
  // These handlers are placeholders for future metadata tracking
  // Actual state updates happen in session store metadata cache
};

/**
 * Handle session visibility events (SessionPublicSpectatorEnabled, SessionPublicSpectatorDisabled,
 * SessionPublicPlayerJoinEnabled, SessionPublicPlayerJoinDisabled)
 *
 * @remarks
 * These events update session visibility settings for public access.
 */
export const handleVisibilityEvent = (
  draft: Session,
  event:
    | Extract<SessionEvent, { type: 'SessionPublicSpectatorEnabled' }>
    | Extract<SessionEvent, { type: 'SessionPublicSpectatorDisabled' }>
    | Extract<SessionEvent, { type: 'SessionPublicPlayerJoinEnabled' }>
    | Extract<SessionEvent, { type: 'SessionPublicPlayerJoinDisabled' }>,
): void => {
  switch (event.type) {
    case 'SessionPublicSpectatorEnabled': {
      draft.isPublicSpectator = true;
      break;
    }
    case 'SessionPublicSpectatorDisabled': {
      draft.isPublicSpectator = false;
      break;
    }
    case 'SessionPublicPlayerJoinEnabled': {
      draft.isPublicPlayerJoin = true;
      break;
    }
    case 'SessionPublicPlayerJoinDisabled': {
      draft.isPublicPlayerJoin = false;
      break;
    }
  }
};

/**
 * Handle join request events (JoinRequestCreated, JoinRequestApproved, JoinRequestRejected)
 *
 * @remarks
 * These events track the join workflow but don't directly modify session state.
 * The final join state is reflected by PlayerJoined/SpectatorJoined events.
 * These handlers are placeholders for potential workflow state tracking.
 */
export const handleJoinRequestEvent = (_draft: Session, _event: SessionEvent): void => {
  // Join requests are workflow events, final state is in PlayerJoined/SpectatorJoined
  // These handlers are placeholders for potential request state tracking
};

/**
 * Handle ImageAdded event - add image reference to appropriate entity
 */
function handleImageAdded(
  draft: Session,
  event: Extract<SessionEvent, { type: 'ImageAdded' }>,
): void {
  const { imageId, entityType, entityId, isPrimary } = event.payload;
  if (entityType === 'session') {
    if (!draft.imageIds.includes(imageId)) {
      draft.imageIds.push(imageId);
    }
  } else if (entityType === 'round') {
    const roundIndex = draft.rounds.findIndex((r) => r.id === entityId);
    if (roundIndex !== -1 && !draft.rounds[roundIndex]?.imageIds.includes(imageId)) {
      draft.rounds[roundIndex]?.imageIds.push(imageId);
    }
  } else if (entityType === 'player' && isPrimary) {
    const playerIndex = draft.players.findIndex((p) => p.id === entityId);
    if (playerIndex !== -1) {
      draft.players[playerIndex].avatarImageId = imageId;
    }
  }
}

/**
 * Handle ImageRemoved event - remove image reference from all entities
 */
function handleImageRemoved(
  draft: Session,
  event: Extract<SessionEvent, { type: 'ImageRemoved' }>,
): void {
  const { imageId } = event.payload;
  // Remove from session images
  draft.imageIds = draft.imageIds.filter((id) => id !== imageId);
  // Remove from all rounds
  for (const round of draft.rounds) {
    round.imageIds = round.imageIds.filter((id) => id !== imageId);
  }
  // Remove from player avatars
  for (const player of draft.players) {
    if (player.avatarImageId === imageId) {
      player.avatarImageId = undefined;
    }
  }
}

/**
 * Handle ImagePrimarySet event - set image as primary/avatar
 */
function handleImagePrimarySet(
  draft: Session,
  event: Extract<SessionEvent, { type: 'ImagePrimarySet' }>,
): void {
  const { entityType, entityId, imageId } = event.payload;
  if (entityType === 'player') {
    const playerIndex = draft.players.findIndex((p) => p.id === entityId);
    if (playerIndex !== -1) {
      draft.players[playerIndex].avatarImageId = imageId;
    }
  }
  // For game/team/group, primary image is stored in gameDefinitions/teamPresets/playerGroups tables, not session
}

/**
 * Handle image events (ImageAdded, ImageRemoved, ImageCaptionChanged, ImagePrimarySet)
 *
 * @remarks
 * These events update image associations in session/player/round state.
 * Actual image data is stored in the images table, these events just track references.
 */
export const handleImageEvent = (draft: Session, event: SessionEvent): void => {
  if (
    event.type !== 'ImageAdded' &&
    event.type !== 'ImageRemoved' &&
    event.type !== 'ImageCaptionChanged' &&
    event.type !== 'ImagePrimarySet'
  ) {
    return;
  }
  switch (event.type) {
    case 'ImageAdded': {
      handleImageAdded(draft, event);
      break;
    }
    case 'ImageRemoved': {
      handleImageRemoved(draft, event);
      break;
    }
    case 'ImageCaptionChanged': {
      // Caption changes don't affect session state, only image metadata
      // This is handled in the images table
      break;
    }
    case 'ImagePrimarySet': {
      handleImagePrimarySet(draft, event);
      break;
    }
  }
};
